import { clonePoint } from "./shared";
/**
 * P3/D-SYNC-13: the single clone SSOT. Every nested config object is
 * deep-cloned (mirrors history.ts) so copy/paste/duplicate can never share
 * mutable state with the source drawing. Scalar overlays (id, symbol, …)
 * ride along via the spread.
 */
export function cloneDrawingSnapshot(drawing, placement) {
    return {
        ...drawing,
        points: drawing.points.map(clonePoint),
        style: { ...drawing.style },
        fibLevels: drawing.fibLevels ? [...drawing.fibLevels] : undefined,
        fibLevelConfigs: drawing.fibLevelConfigs ? drawing.fibLevelConfigs.map((c) => ({ ...c })) : undefined,
        segmentStyles: drawing.segmentStyles
            ? Object.fromEntries(Object.entries(drawing.segmentStyles).map(([key, value]) => [key, { ...value }]))
            : undefined,
        segmentVisible: drawing.segmentVisible ? { ...drawing.segmentVisible } : undefined,
        riskReward: drawing.riskReward ? { ...drawing.riskReward } : undefined,
        visibility: drawing.visibility
            ? {
                ...drawing.visibility,
                timeframes: drawing.visibility.timeframes ? [...drawing.visibility.timeframes] : undefined,
                symbols: drawing.visibility.symbols ? [...drawing.visibility.symbols] : undefined,
            }
            : undefined,
        alertCondition: drawing.alertCondition ? { ...drawing.alertCondition } : undefined,
        alertConditions: drawing.alertConditions?.map((c) => ({ ...c })),
        waveLabels: drawing.waveLabels ? [...drawing.waveLabels] : undefined,
        harmonicRatios: drawing.harmonicRatios
            ? {
                ...drawing.harmonicRatios,
                targetRatios: drawing.harmonicRatios.targetRatios?.map((band) => [...band]),
            }
            : undefined,
        cycleParams: drawing.cycleParams ? { ...drawing.cycleParams } : undefined,
        groupId: drawing.groupId,
        text: drawing.text,
        label: drawing.label,
        symbol: drawing.symbol,
        timeframe: drawing.timeframe,
        paneId: placement?.paneId ?? drawing.paneId,
        yScaleId: placement?.yScaleId ?? drawing.yScaleId,
    };
}
/** P3/D-06 defaults: every paste walks a fixed, predictable data step. */
export const DEFAULT_PASTE_BARS = 5;
export const DEFAULT_PASTE_TICKS = 5;
/**
 * P3: derive the ms-per-bar from actual plot data (median of recent date gaps)
 * so weekly/monthly timeframes stay correct without a TF→ms lookup table.
 * Falls back to the explicit value, then the legacy 1-minute default.
 */
export function resolveBarIntervalMs(plotData, explicitMs) {
    if (plotData) {
        const gaps = [];
        const start = Math.max(0, plotData.length - 6);
        for (let i = Math.max(1, start); i < plotData.length; i += 1) {
            const prev = plotData[i - 1]?.date;
            const curr = plotData[i]?.date;
            const prevMs = prev instanceof Date ? prev.getTime() : typeof prev === "number" ? prev : NaN;
            const currMs = curr instanceof Date ? curr.getTime() : typeof curr === "number" ? curr : NaN;
            if (Number.isFinite(prevMs) && Number.isFinite(currMs) && currMs > prevMs) {
                gaps.push(currMs - prevMs);
            }
        }
        if (gaps.length > 0) {
            gaps.sort((a, b) => a - b);
            return gaps[Math.floor(gaps.length / 2)];
        }
    }
    return typeof explicitMs === "number" && Number.isFinite(explicitMs) && explicitMs > 0 ? explicitMs : 60000;
}
function isFiniteNumber(value) {
    return typeof value === "number" && Number.isFinite(value);
}
function isPlainObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/**
 * BL-12: schema-validate an untrusted clipboard snapshot BEFORE it reaches
 * offset/dispatch. Rejects: non-objects, missing/empty id, missing/empty
 * type, non-array points, points with non-finite numeric anchors.
 * (offsetDrawingByPixels would throw on `.map`/Object.entries otherwise.)
 */
export function isValidDrawingSnapshot(value) {
    if (!isPlainObject(value))
        return false;
    const snapshot = value;
    if (typeof snapshot.id !== "string" || snapshot.id.length === 0)
        return false;
    if (typeof snapshot.type !== "string" || snapshot.type.length === 0)
        return false;
    if (!Array.isArray(snapshot.points))
        return false;
    for (const point of snapshot.points) {
        if (!isPlainObject(point))
            return false;
        for (const key of ["x", "y", "xIndexFloat", "yPrice", "timeMsHint"]) {
            const field = point[key];
            if (field !== undefined && !isFiniteNumber(field))
                return false;
        }
    }
    return true;
}
/**
 * P3/D-06/D-CORE-08: offset a drawing for paste/duplicate/clone-handle.
 *
 * - Pixel render coords (`x`/`y`) always shift by the pixel intent.
 * - Data anchors shift EXACTLY when scales are present
 *   (`xScaleInvert(xScale(x) + dx)`, `yScaleInvert(yScale(y) + dy)`).
 * - Without scales, anchors shift by fixed DATA units (+N bars / +M ticks) —
 *   pixel amounts are NEVER added to prices, indices, or millisecond hints.
 * - Without scales AND without a tickSize, `yPrice` is left untouched rather
 *   than corrupted (better same-price than wrong-price).
 */
export function offsetDrawingByPixels(drawing, xOffset, yOffset, xScale, xScaleInvert, yScaleInvert, barIntervalMs = 60000, placement, options) {
    const now = Date.now();
    const snapshot = cloneDrawingSnapshot(drawing, placement);
    const defaultBars = options?.defaultBars ?? DEFAULT_PASTE_BARS;
    const defaultTicks = options?.defaultTicks ?? DEFAULT_PASTE_TICKS;
    const yScale = options?.yScale ?? null;
    const tickSize = options?.tickSize;
    const intervalMs = resolveBarIntervalMs(options?.plotData, barIntervalMs);
    return {
        ...snapshot,
        id: `${drawing.id}-clone-${now}`,
        points: snapshot.points.map((point) => {
            // X anchor: exact pixel math with scales, else a fixed bar step.
            // barsShift is derived ONCE so xIndexFloat and timeMsHint agree.
            let nextXIndex = point.xIndexFloat;
            let barsShift = defaultBars;
            if (isFiniteNumber(point.xIndexFloat)) {
                if (xScale && xScaleInvert) {
                    const inverted = xScaleInvert(xScale(point.xIndexFloat) + xOffset);
                    if (isFiniteNumber(inverted)) {
                        barsShift = inverted - point.xIndexFloat;
                        nextXIndex = inverted;
                    }
                    else {
                        nextXIndex = point.xIndexFloat + defaultBars;
                    }
                }
                else {
                    nextXIndex = point.xIndexFloat + defaultBars;
                }
            }
            else {
                nextXIndex = undefined;
            }
            // Y anchor: exact single-invert with a forward scale, else ticks,
            // else untouched (never pixel-corrupt the price).
            let nextYPrice = point.yPrice;
            if (isFiniteNumber(point.yPrice)) {
                if (yScale && yScaleInvert) {
                    nextYPrice = yScaleInvert(yScale(point.yPrice) + yOffset);
                }
                else if (typeof tickSize === "number" && Number.isFinite(tickSize)) {
                    nextYPrice = point.yPrice + defaultTicks * tickSize;
                }
            }
            else {
                nextYPrice = undefined;
            }
            return {
                x: point.x + xOffset,
                y: point.y + yOffset,
                xIndexFloat: nextXIndex,
                timeMsHint: isFiniteNumber(point.timeMsHint)
                    ? point.timeMsHint + barsShift * intervalMs
                    : undefined,
                yPrice: nextYPrice,
            };
        }),
        style: { ...snapshot.style },
        fibLevels: snapshot.fibLevels ? [...snapshot.fibLevels] : undefined,
        fibLevelConfigs: snapshot.fibLevelConfigs ? snapshot.fibLevelConfigs.map((c) => ({ ...c })) : undefined,
        segmentStyles: snapshot.segmentStyles
            ? Object.fromEntries(Object.entries(snapshot.segmentStyles).map(([key, value]) => [key, { ...value }]))
            : undefined,
        segmentVisible: snapshot.segmentVisible ? { ...snapshot.segmentVisible } : undefined,
        riskReward: snapshot.riskReward ? { ...snapshot.riskReward } : undefined,
        visibility: snapshot.visibility ? { ...snapshot.visibility } : undefined,
        waveLabels: snapshot.waveLabels ? [...snapshot.waveLabels] : undefined,
        locked: false,
        visible: true,
        clonedFrom: drawing.id,
        zIndex: (drawing.zIndex ?? -1) + 1,
        createdAt: now,
        updatedAt: now,
    };
}
